import { DispensaryStorefrontPlaceholder } from "@the-drop/ui";
import { useRef, useState } from "react";
import {
  deleteDispensaryStorefrontBanner,
  dispensaryStorefrontBannerPreviewUrl,
  STOREFRONT_BANNER_MAX_BYTES,
  uploadDispensaryStorefrontBanner,
} from "../lib/dispensary-account-api";

/**
 * Storefront-banner settings for one dispensary (migration 050): live preview
 * of what the customer menu shows (the uploaded image, or the bundled
 * placeholder illustration), plus upload / remove.
 *
 * Whether a custom banner exists is discovered from the public image endpoint
 * itself — the `<img>` either loads (custom) or 404s (placeholder) — so the
 * card needs no extra "has banner" API.
 */
export function StorefrontBannerCard({
  dispensaryId,
  publicSlug,
  bannerUpdatedAt,
  getToken,
}: {
  dispensaryId: string;
  publicSlug: string | null;
  /**
   * Content-derived cache-buster from the API when it looked the banner up.
   * `null`/`undefined` means "unknown" as far as this card is concerned (the
   * admin list and older APIs do not look it up), never "no banner": the
   * preview always probes and falls back to the placeholder on a 404.
   */
  bannerUpdatedAt?: string | null;
  getToken: () => Promise<string | null>;
}) {
  /**
   * Cache-buster for the preview `<img>`. When the API supplied the banner's
   * upload timestamp it is the same content-derived version the customer
   * storefront uses: it changes only when the banner does, so the browser
   * reuses the cached image across reloads (the endpoint sends `max-age=300`)
   * yet never shows a stale one. When the timestamp is unknown, seed per
   * mount so a stale cached copy is never shown. Bumped from the PUT
   * response after every upload.
   */
  const [previewVersion, setPreviewVersion] = useState(() => bannerUpdatedAt ?? `probe-${Date.now()}`);
  /** Always probe; falls to false when the public endpoint 404s (no custom banner). */
  const [hasCustomBanner, setHasCustomBanner] = useState(true);
  const [busy, setBusy] = useState<"upload" | "remove" | null>(null);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  async function onFileChosen(file: File | undefined) {
    if (!file) return;
    setMessage(null);
    setBusy("upload");
    try {
      const result = await uploadDispensaryStorefrontBanner(getToken, dispensaryId, file);
      if (!result.ok) {
        const maxMb = Math.round(STOREFRONT_BANNER_MAX_BYTES / (1024 * 1024));
        setMessage({
          kind: "error",
          text:
            result.error === "unsupported_type"
              ? "That file type is not supported. Use a JPEG, PNG, or WebP image."
              : result.error === "too_large"
                ? `That image is too large. The banner limit is ${maxMb} MB.`
                : "The upload failed. Check your connection and try again.",
        });
        return;
      }
      setHasCustomBanner(true);
      setPreviewVersion(result.updatedAt);
      setMessage({ kind: "ok", text: "Banner updated. Customers see it on your menu right away." });
    } finally {
      setBusy(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function onRemove() {
    setMessage(null);
    setBusy("remove");
    try {
      const ok = await deleteDispensaryStorefrontBanner(getToken, dispensaryId);
      if (!ok) {
        setMessage({ kind: "error", text: "Could not remove the banner. Try again." });
        return;
      }
      setHasCustomBanner(false);
      setPreviewVersion(`removed-${Date.now()}`);
      setMessage({ kind: "ok", text: "Banner removed. Your menu shows the default illustration." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="border-t border-[#F0F0F0] px-5 py-4">
      <h4 className="text-2xs font-semibold uppercase tracking-wide text-[#AAAAAA]">
        Storefront banner
      </h4>
      <p className="mt-1 text-xs text-[#888888]">
        Shown at the top of your public menu. JPEG, PNG, or WebP up to{" "}
        {Math.round(STOREFRONT_BANNER_MAX_BYTES / (1024 * 1024))} MB; wide images (about 3:1) look
        best. Without an upload, customers see the default illustration below.
      </p>

      <div className="mt-3 h-32 overflow-hidden rounded-xl border border-[#EBEBEB] bg-[#FAFAFA]">
        {publicSlug && hasCustomBanner ? (
          <img
            src={dispensaryStorefrontBannerPreviewUrl(publicSlug, previewVersion)}
            alt="Current storefront banner"
            className="h-full w-full object-cover"
            onError={() => setHasCustomBanner(false)}
          />
        ) : (
          <DispensaryStorefrontPlaceholder className="h-full w-full" title="" />
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => void onFileChosen(e.target.files?.[0])}
        />
        <button
          type="button"
          disabled={busy != null}
          onClick={() => fileInputRef.current?.click()}
          className="rounded-lg bg-[#2F5D50] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#264a40] disabled:opacity-50"
        >
          {busy === "upload" ? "Uploading…" : "Upload banner image"}
        </button>
        {publicSlug && hasCustomBanner ? (
          <button
            type="button"
            disabled={busy != null}
            onClick={() => void onRemove()}
            className="rounded-lg border border-[#EBEBEB] px-3 py-1.5 text-xs font-medium text-[#555555] hover:bg-[#FAFAFA] disabled:opacity-50"
          >
            {busy === "remove" ? "Removing…" : "Use default"}
          </button>
        ) : null}
        {!publicSlug ? (
          <span className="text-2xs text-[#AAAAAA]">
            Preview requires a public menu slug; uploads still save.
          </span>
        ) : null}
      </div>

      {message ? (
        <p
          className={`mt-2 text-xs ${message.kind === "ok" ? "text-[#2D6B2D]" : "text-[#991B1B]"}`}
          role="status"
        >
          {message.text}
        </p>
      ) : null}
    </div>
  );
}
